import { X, Keyboard } from 'lucide-react';
import { useEffect } from 'react';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const shortcuts = [
  { keys: ['V'], description: 'Toggle view mode (Pressure/Opportunity)' },
  { keys: ['T'], description: 'Toggle trajectory arrows' },
  { keys: ['R'], description: 'Refresh data' },
  { keys: ['Esc'], description: 'Close panel' },
  { keys: ['+', '='], description: 'Zoom in' },
  { keys: ['-'], description: 'Zoom out' },
  { keys: ['0'], description: 'Reset zoom' },
  { keys: ['/'], description: 'Search jurisdictions' },
  { keys: ['?'], description: 'Show this help' },
];

const HelpModal = ({ isOpen, onClose }: HelpModalProps) => {
  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative glass-strong rounded-xl shadow-card-elevated max-w-md w-full mx-4 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Keyboard className="h-5 w-5 text-brand" />
            <h2 className="text-lg font-semibold text-text-primary">Keyboard Shortcuts</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-text-secondary hover:text-text-primary hover:bg-white/10 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="space-y-3">
            {shortcuts.map((shortcut, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-1.5"
              >
                <span className="text-sm text-text-secondary">
                  {shortcut.description}
                </span>
                <div className="flex items-center gap-1">
                  {shortcut.keys.map((key, keyIndex) => (
                    <span key={keyIndex} className="flex items-center gap-1">
                      {keyIndex > 0 && (
                        <span className="text-text-tertiary text-xs">or</span>
                      )}
                      <kbd className="px-2 py-1 bg-white/5 border border-white/10 rounded text-xs font-mono text-text-primary">
                        {key}
                      </kbd>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-white/10 bg-white/5 rounded-b-xl">
          <p className="text-xs text-text-tertiary text-center">
            Press <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-text-secondary">Esc</kbd> to close
          </p>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
